/**
 * Virtual Asset Test Mock Data Panel
 *
 * 开发环境下的虚拟资产知识测试Mock工具面板
 * 提供可视化的界面用于快速填充问卷
 *
 * @module mockDataGenerator.demo
 */
import { Button, InputNumber } from "antd";
import { FormInstance } from "antd/lib";
import { useState } from "react";
import {
  calculateVirtualAssetScore,
  generateRandomScore,
  generateVirtualAssetMockData,
  QuizQuestion,
  VIRTUAL_ASSET_PRESETS,
} from "./mockDataGenerator";

interface VirtualAssetMockDataPanelProps {
  form: FormInstance;
  questions?: QuizQuestion[];
}

/**
 * 虚拟资产测试Mock数据生成工具面板
 */
export function VirtualAssetMockDataPanel({
  form,
  questions,
}: VirtualAssetMockDataPanelProps) {
  const [visible, setVisible] = useState(false);
  const [targetScore, setTargetScore] = useState(70);
  const [currentScore, setCurrentScore] = useState(0);

  if (!questions || questions.length === 0) {
    return null;
  }

  /**
   * 填充表单数据
   */
  const fillFormData = (score: number) => {
    const mockData = generateVirtualAssetMockData(questions, {
      targetScore: score,
    });

    form.setFieldsValue(mockData);

    // 计算实际得分
    const actualScore = calculateVirtualAssetScore(questions, mockData);
    setCurrentScore(actualScore);

    console.log("✅ 已填充虚拟资产测试数据");
    console.log("📊 目标分数:", score);
    console.log("📊 实际分数:", actualScore);
    console.log("📝 表单数据:", mockData);
  };

  /**
   * 预设场景：不及格（30分）
   */
  const handleFillFailLow = () => {
    fillFormData(VIRTUAL_ASSET_PRESETS.FAIL_LOW.targetScore);
  };

  /**
   * 预设场景：不及格（60分）
   */
  const handleFillFailHigh = () => {
    fillFormData(VIRTUAL_ASSET_PRESETS.FAIL_HIGH.targetScore);
  };

  /**
   * 预设场景：及格（70分）
   */
  const handleFillPass = () => {
    fillFormData(VIRTUAL_ASSET_PRESETS.PASS.targetScore);
  };

  /**
   * 预设场景：良好（80分）
   */
  const handleFillGood = () => {
    fillFormData(VIRTUAL_ASSET_PRESETS.GOOD.targetScore);
  };

  /**
   * 预设场景：优秀（100分）
   */
  const handleFillExcellent = () => {
    fillFormData(VIRTUAL_ASSET_PRESETS.EXCELLENT.targetScore);
  };

  /**
   * 自定义分数
   */
  const handleFillCustom = () => {
    fillFormData(targetScore);
  };

  /**
   * 随机生成
   */
  const handleFillRandom = () => {
    const randomScore = generateRandomScore();
    setTargetScore(randomScore);
    fillFormData(randomScore);
  };

  /**
   * 清空表单
   */
  const handleClear = () => {
    form.resetFields();
    setCurrentScore(0);
    console.log("🗑️ 已清空表单");
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            📚
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "360px",
            maxHeight: "700px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              📚 虚拟资产测试 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "600px" }}
          >
            {/* 当前分数显示 */}
            {currentScore > 0 && (
              <div
                className={`border rounded p-3 ${
                  currentScore >= 70
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div
                  className={`text-sm font-semibold ${
                    currentScore >= 70 ? "text-green-800" : "text-red-800"
                  }`}
                >
                  当前分数
                </div>
                <div
                  className={`text-2xl font-bold mt-1 ${
                    currentScore >= 70 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {currentScore} 分
                </div>
                <div
                  className={`text-xs mt-1 ${
                    currentScore >= 70 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {currentScore >= 70
                    ? "✅ 通过（≥70分）"
                    : "❌ 未通过（<70分）"}
                </div>
              </div>
            )}

            {/* 预设场景 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🎯 预设场景
              </div>

              <Button
                size="small"
                block
                onClick={handleFillFailLow}
                className="text-left"
              >
                <div className="flex justify-between items-center w-full">
                  <span>{VIRTUAL_ASSET_PRESETS.FAIL_LOW.name}</span>
                  <span className="text-xs text-red-500">未通过</span>
                </div>
              </Button>

              <Button
                size="small"
                block
                onClick={handleFillFailHigh}
                className="text-left"
              >
                <div className="flex justify-between items-center w-full">
                  <span>{VIRTUAL_ASSET_PRESETS.FAIL_HIGH.name}</span>
                  <span className="text-xs text-red-500">未通过</span>
                </div>
              </Button>

              <Button
                size="small"
                block
                onClick={handleFillPass}
                className="text-left"
              >
                <div className="flex justify-between items-center w-full">
                  <span>{VIRTUAL_ASSET_PRESETS.PASS.name}</span>
                  <span className="text-xs text-green-500">通过</span>
                </div>
              </Button>

              <Button
                size="small"
                block
                onClick={handleFillGood}
                className="text-left"
              >
                <div className="flex justify-between items-center w-full">
                  <span>{VIRTUAL_ASSET_PRESETS.GOOD.name}</span>
                  <span className="text-xs text-green-500">通过</span>
                </div>
              </Button>

              <Button
                size="small"
                block
                onClick={handleFillExcellent}
                className="text-left"
              >
                <div className="flex justify-between items-center w-full">
                  <span>{VIRTUAL_ASSET_PRESETS.EXCELLENT.name}</span>
                  <span className="text-xs text-green-500">通过</span>
                </div>
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 自定义分数 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🎲 自定义分数
              </div>

              <div className="flex items-center gap-2">
                <InputNumber
                  size="small"
                  min={0}
                  max={100}
                  step={10}
                  value={targetScore}
                  onChange={(val) => setTargetScore(val || 0)}
                  style={{ width: "100px" }}
                  addonAfter="分"
                />
                <Button size="small" onClick={handleFillCustom} type="primary">
                  填充
                </Button>
              </div>

              <div className="text-xs text-gray-500">
                可选范围: 0, 10, 20, ..., 100 分（10分一档）
              </div>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 其他操作 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🔧 其他操作
              </div>
              <Button size="small" block onClick={handleFillRandom}>
                随机生成
              </Button>
              <Button size="small" block danger onClick={handleClear}>
                清空表单
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 使用说明 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                ℹ️ 使用说明
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• 选择预设场景快速填充</div>
                <div>• 输入自定义分数精确控制</div>
                <div>• 每道题10分，共{questions.length}道题</div>
                <div>• 总分范围：0-100分</div>
                <div>• 及格分数：70分（答对7道题）</div>
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="font-semibold mb-1">评分规则：</div>
                  <div>• 答对：+10分</div>
                  <div>• 答错：+0分</div>
                  <div>• 70分以上通过测试</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default VirtualAssetMockDataPanel;
